// src/components/Register.js

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { makeApiRequest } from '../api';
import zxcvbn from 'zxcvbn';

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const evaluatePasswordStrength = (password) => {
    const { score } = zxcvbn(password);
    const strengthLevels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    setPasswordStrength(strengthLevels[score]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (password !== confirmPass) {
      toast.error('Passwords do not match.');
      return;
    }

    try {
      const response = await makeApiRequest('/users/register', 'POST', {
        email,
        password,
        access_key: accessKey,
      });

      if (response.success) {
        toast.success('Registration successful! Logging in...');
        navigate('/login');
      } else {
        toast.error(response.error || 'Failed to register. Please try again.');
      }
    } catch (error) {
      toast.error(
        'Failed to register. Please check your information and try again.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="bg-foreground shadow-lg rounded-lg p-8 w-full max-w-md">
        <h2 className="text-2xl font-semibold text-primary text-center mb-6">
          Register
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          <div>
            <label
              htmlFor="email"
              className="block text-secondary font-medium mb-1"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              className="w-full px-4 py-2 border border-secondary rounded focus:outline-none focus:ring-2 focus:ring-accent"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block text-secondary font-medium mb-1"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              className="w-full px-4 py-2 border border-secondary rounded focus:outline-none focus:ring-2 focus:ring-accent"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                evaluatePasswordStrength(e.target.value);
              }}
              required
            />
            <div className="mt-2 text-sm">
              Password Strength:{' '}
              <span
                className={`font-semibold ${passwordStrength === 'Strong' ? 'text-green-600' : passwordStrength === 'Good' ? 'text-yellow-500' : 'text-red-500'}`}
              >
                {passwordStrength}
              </span>
            </div>
          </div>
          <div>
            <label
              htmlFor="confirm-password"
              className="block text-secondary font-medium mb-1"
            >
              Confirm Password
            </label>
            <input
              type="password"
              id="confirm-password"
              className="w-full px-4 py-2 border border-secondary rounded focus:outline-none focus:ring-2 focus:ring-accent"
              value={confirmPass}
              onChange={(e) => setConfirmPass(e.target.value)}
              required
            />
          </div>
          <div>
            <label
              htmlFor="access-key"
              className="block text-secondary font-medium mb-1"
            >
              Access Key
            </label>
            <input
              type="text"
              id="access-key"
              className="w-full px-4 py-2 border border-secondary rounded focus:outline-none focus:ring-2 focus:ring-accent"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-primary text-foreground py-2 rounded hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Register
          </button>
        </form>
        <p className="mt-6 text-center text-secondary">
          Already have an account?{' '}
          <a href="/login" className="text-link hover:underline">
            Log in
          </a>
        </p>
      </div>
    </div>
  );
}

export default Register;
